import { useRef, useState, useMemo, useCallback } from 'react';
import type { PageVariable } from '@/pages/Meta/Connection/types/meta.types';

// ── helpers ──────────────────────────────────────────────────────────────────

function escapeHtml(s: string) {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/**
 * Builds the innerHTML for the highlight div.
 * Regular text → dark (via inherited color).
 * {{VAR}} tokens → inline blue chip span.
 * IMPORTANT: No padding, borders, or font-weight changes can be applied to the span,
 * or else it will break exact alignment with the invisible textarea underneath.
 */
function buildHighlightHtml(text: string, singleLine: boolean): string {
    const parts = text.split(/(\{\{[^}]+\}\})/g);
    return parts
        .map(part => {
            if (/^\{\{[^}]+\}\}$/.test(part)) {
                return (
                    `<span style="` +
                    `background:#dbeafe;` +
                    `color:#1d4ed8;` +
                    `border-radius:3px;` + // Border radius is safe because it only affects the background box
                    `">${escapeHtml(part)}</span>`
                );
            }
            // preserve newlines for multi-line mode and handle trailing newline scrolling fix
            const escaped = escapeHtml(part);
            return singleLine ? escaped : escaped.replace(/\n/g, '<br>');
        })
        .join('');
}

// ── component ─────────────────────────────────────────────────────────────────

interface SmartTextFieldProps {
    value: string;
    onChange: (val: string) => void;
    variables?: PageVariable[];
    placeholder?: string;
    rows?: number;
    /** true → single-line URL mode */
    singleLine?: boolean;
    className?: string;
    inputClassName?: string;
}

export default function SmartTextField({
    value,
    onChange,
    variables = [],
    placeholder,
    rows = 3,
    singleLine = false,
    className,
    inputClassName,
}: SmartTextFieldProps) {
    const [focused, setFocused] = useState(false);
    const taRef = useRef<HTMLTextAreaElement>(null);
    const highlightRef = useRef<HTMLDivElement>(null);

    // Keep scroll positions in sync
    const syncScroll = useCallback(() => {
        if (taRef.current && highlightRef.current) {
            highlightRef.current.scrollTop = taRef.current.scrollTop;
            highlightRef.current.scrollLeft = taRef.current.scrollLeft;
        }
    }, []);

    const highlightHtml = useMemo(
        () => buildHighlightHtml(value, singleLine),
        [value, singleLine],
    );

    // Insert {{KEY}} at the current cursor position
    const insertVariable = useCallback((key: string) => {
        const el = taRef.current;
        if (!el) return;
        const start = el.selectionStart ?? value.length;
        const end = el.selectionEnd ?? value.length;
        const token = `{{${key}}}`;
        onChange(value.slice(0, start) + token + value.slice(end));
        setTimeout(() => {
            el.focus();
            el.setSelectionRange(start + token.length, start + token.length);
        }, 0);
    }, [value, onChange]);

    // Auto-filter suggestions when the user types "{{..."
    const suggestionFilter = useMemo(() => {
        if (!focused) return '';
        const idx = value.lastIndexOf('{{');
        if (idx !== -1) {
            const after = value.slice(idx + 2);
            if (!after.includes('}}')) return after.toLowerCase();
        }
        return '';
    }, [value, focused]);

    const suggestions = useMemo(
        () => variables.filter(v => v.key.toLowerCase().includes(suggestionFilter)),
        [variables, suggestionFilter],
    );

    const showSuggestions = focused && suggestions.length > 0;

    // ── shared typography that MUST match between the two layers ──────────────
    const font = singleLine
        ? 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace'
        : 'inherit';
    const fontSize = singleLine ? '12px' : '14px';
    const paddingTop = singleLine ? '5px' : '8px';
    const paddingX = '12px';
    const lineHeight = '1.5';
    const height = singleLine ? '30px' : undefined;

    const commonStyle: React.CSSProperties = {
        fontFamily: font,
        fontSize,
        lineHeight,
        padding: `${paddingTop} ${paddingX}`,
        margin: 0,
        border: 'none',
        outline: 'none',
        boxSizing: 'border-box',
        whiteSpace: singleLine ? 'nowrap' : 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        width: '100%',
        height,
    };

    return (
        <div
            className={`
                relative w-full rounded-md border border-input bg-background
                ${focused ? 'ring-2 ring-ring ring-offset-2 ring-offset-background' : ''}
                ${className ?? ''}
            `}
            style={{ position: 'relative' }}
        >

            {/*
             * ── HIGHLIGHT LAYER (lower z-index, pointer-events:none) ──
             * color: #1e293b → regular text is DARK and VISIBLE.
             * {{VAR}} spans → blue background + blue text.
             * We place it exactly underneath the textarea. The ending <br> handles the last empty line.
             */}
            <div
                ref={highlightRef}
                aria-hidden="true"
                style={{
                    ...commonStyle,
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    pointerEvents: 'none',
                    // regular text color — visible through the transparent textarea
                    color: '#1e293b',
                    overflow: singleLine ? 'hidden' : 'auto',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    borderColor: 'transparent'
                }}
                dangerouslySetInnerHTML={{ __html: highlightHtml + (singleLine || !value.endsWith('\n') ? '' : '<br>') }}
            />

            {/*
             * ── TEXTAREA (higher z-index, handles all input) ──
             * color: transparent → text is invisible, only caret is shown.
             * background: transparent → Lets highlight layer show through.
             */}
            <textarea
                ref={taRef}
                value={value}
                rows={singleLine ? 1 : rows}
                placeholder={placeholder}
                onChange={e => onChange(e.target.value)}
                onScroll={syncScroll}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 160)}
                style={{
                    ...commonStyle,
                    position: 'relative',
                    zIndex: 2,
                    resize: 'none',
                    display: 'block',
                    color: 'transparent', // text is invisible; cursor remains visible
                    caretColor: '#0f172a',
                    background: 'transparent',
                    overflow: singleLine ? 'hidden' : 'auto',
                }}
                className={`
                    w-full bg-transparent border-none focus:outline-none focus:ring-0
                    placeholder:text-muted-foreground
                    ${inputClassName ?? ''}
                `}
            />

            {/* ── Variable suggestions panel ── */}
            {showSuggestions && (
                <div
                    className="absolute left-0 right-0 z-50 mt-0.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
                    style={{ top: '100%' }}
                >
                    <div className="px-3 py-1.5 border-b border-gray-100 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block flex-shrink-0" />
                        <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                            Variáveis disponíveis — clique para inserir
                        </p>
                    </div>
                    <div className="p-2 flex flex-wrap gap-1.5 max-h-44 overflow-y-auto">
                        {suggestions.map(v => (
                            <button
                                key={v.id}
                                type="button"
                                onMouseDown={e => { e.preventDefault(); insertVariable(v.key); }}
                                title={`Valor: ${v.value}`}
                                className="group inline-flex items-center gap-1 px-2 py-0.5
                                    bg-blue-50 hover:bg-blue-100
                                    text-blue-700 border border-blue-200 hover:border-blue-400
                                    rounded-full text-[11px] font-mono font-semibold
                                    transition-all cursor-pointer"
                            >
                                <span className="text-blue-300 text-[9px]">{'{{'}</span>
                                {v.key}
                                <span className="text-blue-300 text-[9px]">{'}}'}</span>
                                {v.value && (
                                    <span className="ml-1 text-[9px] text-blue-400 max-w-[80px] truncate font-normal hidden group-hover:inline">
                                        = {v.value}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
